import { useState } from 'react';
import type { ResultadoCampanha } from './dados';
import { cpResponder, ehPerguntaComoCheguei, respostaComoCheguei } from './dados';

interface Mensagem {
  deQuem: 'voce' | 'oto';
  html: string;
}

interface ChatCampanhaProps {
  resultado: ResultadoCampanha | null;
  nomeBairro?: (id: string) => string;
}

/**
 * Chat "Conversar sobre a campanha" — proto:1654-1662 (`cpChat`/`cpPerguntar`).
 * Motor de resposta local (`cpResponder`/`respostaComoCheguei`), sem chamada
 * a modelo de linguagem: o protótipo também resolve isso com regras sobre o
 * próprio resultado, nunca inventando um número novo.
 */
export function ChatCampanha({ resultado, nomeBairro = (id) => id }: ChatCampanhaProps) {
  const [mensagens, setMensagens] = useState<Mensagem[]>([]);
  const [pergunta, setPergunta] = useState('');

  const perguntar = (texto: string) => {
    const q = texto.trim();
    if (!q || !resultado) return;
    const resposta = ehPerguntaComoCheguei(q)
      ? respostaComoCheguei(resultado)
      : cpResponder(resultado, q, nomeBairro);
    setMensagens((m) => [...m, { deQuem: 'voce', html: q }, { deQuem: 'oto', html: resposta }]);
    setPergunta('');
  };

  // proto:1656 — as sugestões incluem os recortes que o motor de fato executa
  // (`detectarRecorte`): bairro coberto pela campanha e município do cadastro.
  const municipios = resultado ? [...new Set(Object.values(resultado.municipioPorBairro))] : [];
  const sugestoes = resultado
    ? [
        'Quantos responderam?',
        'Como cheguei',
        ...(resultado.bairros[0] ? [`Só quem é de ${nomeBairro(resultado.bairros[0])}`] : []),
        ...(municipios[0] ? [`Só o município de ${municipios[0]}`] : []),
        ...(resultado.bairros.length > 1 ? ['Compare os bairros'] : []),
      ]
    : [];

  return (
    <div className="card" style={{ borderColor: '#B26A00' }}>
      <h2 style={{ color: '#B26A00' }}>
        Conversar sobre a campanha {resultado ? resultado.id : ''}
      </h2>
      <p style={{ fontSize: 12, color: 'var(--ink3)', margin: '0 0 8px' }}>
        Pergunte como chegou ao resultado, peça recortes (&quot;só quem é de Jangurussu&quot;,
        &quot;só o município de Fortaleza&quot;), compare bairros ou uma pergunta específica.
      </p>

      {!resultado && <div className="empty">Nenhum resultado para conversar ainda.</div>}

      {resultado && (
        <div className="grid g21">
          <div>
            <div className="msgbox" style={{ maxHeight: 320, minHeight: 140 }}>
              {mensagens.length === 0 && (
                <div className="empty">
                  Ex.: &quot;quantos responderam?&quot;, &quot;como está a pergunta sobre forças
                  externas só em Jangurussu?&quot;, &quot;compare os bairros&quot;
                </div>
              )}
              {mensagens.map((m, i) =>
                m.deQuem === 'oto' ? (
                  <div key={i} className="msg" style={{ maxWidth: '95%', background: 'var(--warn-soft)' }}>
                    <b style={{ color: '#B26A00' }}>OTO</b>
                    <br />
                    <span dangerouslySetInnerHTML={{ __html: m.html }} />
                  </div>
                ) : (
                  <div key={i} className="msg me">
                    {m.html}
                  </div>
                ),
              )}
            </div>
            <div style={{ display: 'flex', gap: 8, marginTop: 8 }}>
              <input
                className="in"
                placeholder="Pergunte sobre esta campanha…"
                value={pergunta}
                onChange={(e) => setPergunta(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') perguntar(pergunta);
                }}
              />
              <button type="button" className="btn pri" onClick={() => perguntar(pergunta)}>
                Perguntar
              </button>
            </div>
          </div>

          <div className="card flat">
            <h3>Sugestões</h3>
            {sugestoes.map((s) => (
              <button
                type="button"
                key={s}
                className="tk"
                style={{ marginBottom: 6, display: 'block', width: '100%', textAlign: 'left' }}
                onClick={() => perguntar(s)}
              >
                {s}
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
